import React, { useEffect } from 'react';
import { createStructuredSelector } from 'reselect';
import { useDispatch, useSelector } from 'react-redux';
import { graphql } from 'gatsby';
import { getIsOnInitPage } from '@om/redux/selectors/easiplusFuneralPlan/quote';
import { setQuoteProductValue } from '@om/redux/actions/easiplusFuneralPlan/callMeBack';
import { OmApplicationContainer } from '@om/component-library-react';
import IncomeTaxCalculator from './incomeTaxCalculator/IncomeTaxCalculator';
import FindAnAdvisor from './FindAnAdvisor';
import SavingsCalculator from './savingsCalculator/SavingsCalculator';
import PersonalLoansCalculator from './personalLoansCalculator/PersonalLoansCalculator';
import NamPersonalLoansCalculator from './namPersonalLoansCalculator/PersonalLoansCalculator';
import QuotePage from '../applications/easiplusFuneralPlan/quotePage/QuotePage';
import MarketIndicators from './MarketIndicators';
import RetirementCalculator from './retirementCalculator/RetirementCalculator';
import PriceGraph from './priceGraph/PriceGraph';
import BudgetBenchmarkingTool from './budgetBenchmarkingTool/BudgetBenchmarkingTool';
import PastPerformanceStaticQueryWrapper from './pastPerformanceCalculator/PastPerformanceStaticQueryWrapper';
import EducationCalculator from './educationCalculator/EducationCalculator';
import PriceTableStaticQueryWrapper from './priceTable/PriceTabelStaticQueryWrapper';
import DebtRepaymentCalculator from './debtRepaymentCalculator/DebtRepaymentCalculator';
import FundDeclarations from './fundDeclarations/FundDeclarations';
import CustomerSatisfactionSurvey from './customerSatisfactionSurvey/CustomerSatisfactionSurvey'
import InvestmentCalculator from'./investmentCalculator/InvestmentCalculator';
import EkyireAsem from '../applications/westAfricaApps/ghana/applications/digital-funeral/EkyireAsem';
import SavingsPotCalculator from './savingsPotCalculator/SavingsPotCalculator';
import SizotsaFuneralQuotePage from '../applications/sizotsaFuneralCover/quotingJourney/quotePage/SizotsaFuneralQuotePage';
import BwQuotePage from '../applications/kaMetlhaFuneralCover/quotingJourney/quotePage/BwQuotePage';
import DividendCalculator from './DividendCalculator';
import FauluLoanCalculator from './fauluLoanCalculator/FauluLoanCalculator';

const PAST_PERF_CALC = 'PastPerformanceCalculator';

const calculators = [
    'IncomeTaxCalculator',
    'PersonalLoansCalculator',
    'NamDebtCalculator',
    'SavingsCalculator',
    'MarketIndicators',
    'PriceTable',
    'PriceGraph',
    'RetirementCalculator',
    'BudgetBenchmarkingTool',
    'EducationCalculator',
    'DebtRepaymentCalculator',
    'FundDeclarations',
    'PastPerformanceCalculator',
    PAST_PERF_CALC,
    'InvestmentCalculator',
    'SavingsPotCalculator',
    'DividendCalculator',
    'FauluLoanCalculator'
];

const centeredHeadingApps = [
    'EasiPlus',
    'SavingsCalculator',
    'PriceGraph',
    'RetirementCalculator',
    'MarketIndicators',
    'BudgetBenchmarkingTool',
    'CustomerSatisfactionSurvey',
    'EkyireAsem',
    'NamDebtCalculator',
];


const ApplicationContainer = ({
    applicationContainerContent,
    isInContainer,
    onCompleteEvent,
    slot
}) => {
    const dispatch = useDispatch();
    const {efpIsOnQuotePage} = useSelector(
        createStructuredSelector({
            efpIsOnQuotePage: getIsOnInitPage,
        })
    );

    useEffect(() => {
        if (applicationContainerContent) {
            if (application_type && application_type === 'EasiPlus' &&
                applicationContainerContent.call_me_back && applicationContainerContent.call_me_back[0].call_me_back_product) {
                dispatch(setQuoteProductValue(applicationContainerContent.call_me_back[0].call_me_back_product));
            }
        }
    }, []);

    if (!applicationContainerContent) {
        return '';
    }

    const {
        uid,
        has_boundary,
        section_heading,
        section_caption,
        application_type,
        button_text,
        button_url,
        product_offering_one,
        product_offering_two,
        product_offering_three
    } = applicationContainerContent;

    const isCenteredHeading = centeredHeadingApps && centeredHeadingApps.includes(application_type);
    const isCalculator = calculators && calculators.indexOf(application_type) !== -1;

    const applicationType = () => {
        if (application_type === 'EasiPlus') {
            return efpIsOnQuotePage ? 'efp-quote-init-page' : 'efp-quote';
        }
    };

    return (
        <OmApplicationContainer
            id="calculator_container"
            slot={slot}
            isCalculator={isCalculator}
            hasBoundary={has_boundary}
            applicationType={applicationType()}
            isHeadingCentered={isCenteredHeading}>
            {
                section_heading && section_heading.trim() !== '' ? <span slot="section-heading"
                    dangerouslySetInnerHTML={{ __html: section_heading }} /> : ''
            }
            {
                section_caption && section_caption.trim() !== '' ? <span slot="section-caption"
                    dangerouslySetInnerHTML={{ __html: section_caption }} /> : ''
            }
            {
                application_type === 'SavingsCalculator' &&
                <SavingsCalculator twoColumnImageLeftContent={product_offering_one && product_offering_one[0]}
                    tableContent={product_offering_two && product_offering_two[0]} slot="application-content"
                    isInContainer={isInContainer}
                    onCompleteEvent={onCompleteEvent} />
            }

            {
                application_type === 'RetirementCalculator' &&
                <RetirementCalculator twoColumnImageLeftContent={product_offering_one && product_offering_one[0]}
                    tableContent={product_offering_two && product_offering_two[0]} slot="application-content" 
                    isInContainer={isInContainer}
                    onCompleteEvent={onCompleteEvent}/>
            }

            {
                application_type === 'SavingsPotCalculator' &&
                <SavingsPotCalculator 
                    uid={uid}   
                    slot='application-content'
                    isInContainer={isInContainer}
                    twoColumnImageLeftContent={product_offering_one && product_offering_one[0]}
                    productTableContent={product_offering_two && product_offering_two[0]}
                    onCompleteEvent={onCompleteEvent} />
            }

            {/*Calculators, Quote and FAA*/}
            {
                application_type === 'IncomeTaxCalculator'
                && <IncomeTaxCalculator
                    uid={uid}
                    slot='application-content'
                    twoColumnImageLeftContent={product_offering_one && product_offering_one[0]}
                    productTableContent={product_offering_two && product_offering_two[0]}
                    buttonText={button_text}
                    buttonUrl={button_url}
                    onCompleteEvent={onCompleteEvent}
                    isInContainer={isInContainer} />
            }

            {
                application_type === 'FindAnAdviser'
                && <FindAnAdvisor
                    slot="application-content"
                    findAnAdvisorContent={applicationContainerContent} />
            }
            {
                application_type === 'PersonalLoansCalculator'
                && <PersonalLoansCalculator
                    uid={uid}
                    slot='application-content'
                    isInContainer={isInContainer}
                    onCompleteEvent={onCompleteEvent} />
            }
            {
                application_type === 'PriceGraph'
                && <PriceGraph
                    slot='application-content'/>
            }
            {
                application_type === 'EasiPlus'
                && <QuotePage slot='application-content' />
            }
            {
                application_type === 'MarketIndicators' && <MarketIndicators slot='application-content' />
            }
            {
                application_type === 'PriceTable' && <PriceTableStaticQueryWrapper slot='application-content' />
            }
            {
                application_type === 'BudgetBenchmarkingTool' && <BudgetBenchmarkingTool slot='application-content' isInContainer={isInContainer} onCompleteEvent={onCompleteEvent} />
            }
            {
                application_type === 'PastPerformanceCalculator' && <PastPerformanceStaticQueryWrapper slot='application-content' />
            }
            {
                application_type === 'DebtRepaymentCalculator'
                && <DebtRepaymentCalculator
                    uid={uid}
                    slot='application-content'
                    isInContainer={isInContainer}
                    onCompleteEvent={onCompleteEvent}/>
            }
            {
                application_type === 'NamDebtCalculator'
                && <NamPersonalLoansCalculator
                    uid={uid}
                    slot='application-content'
                    isInContainer={isInContainer}
                    onCompleteEvent={onCompleteEvent} />
            }
            {
                application_type === 'EducationCalculator'
                && <EducationCalculator
                    uid={uid}
                    twoColumnImageLeftContent={product_offering_one && product_offering_one[0]}
                    productTableContent={product_offering_two && product_offering_two[0]}
                    slot='application-content'
                    isInContainer={isInContainer}
                    onCompleteEvent={onCompleteEvent} />
            }
            {
                application_type === 'FundDeclarations'
                && <FundDeclarations
                    uid={uid}
                    slot='application-content' />
            }
            {
                application_type === 'CustomerSatisfactionSurvey'
                && <CustomerSatisfactionSurvey
                    slot='application-content' />
            }

            {
                application_type === 'InvestmentCalculator'
                && <InvestmentCalculator
                    twoColumnImageLeftContent={product_offering_one?.[0]}
                    twoColumnImageRightContent={product_offering_three?.[0]}
                    slot='application-content'
                />
            }
            {
                application_type === 'FauluLoanCalculator'
                && <FauluLoanCalculator
                    uid={uid}
                    slot='application-content'
                    isInContainer={isInContainer}
                    onCompleteEvent={onCompleteEvent} />
            }

            { application_type === 'EkyireAsem' && <EkyireAsem slot='application-content'/> }
            {
                application_type === 'BwFuneralQuote'
                && <BwQuotePage slot='application-content' />
            }
            {application_type === 'SizotsaFuneral' && <SizotsaFuneralQuotePage slot="application-content" />}
        </OmApplicationContainer>
    );
}

export const pageQuery = graphql`
fragment ApplicationContainer on Contentstack_application_container {
    id
    uid
    has_boundary
    section_heading
    section_caption
    application_type
    button_text
    button_url {
        ...Link
    }
    product_offering_one {
         ...TwoColumnImageLeft
    }
    product_offering_two {
        ...Table
    }
    call_me_back {
        ...CallMeBack
    }
}
`;

export default ApplicationContainer;
